﻿// ----------------------------------------------------------------------
// <copyright file="Izenda.TenantDetail.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import React, { Component } from 'react';
import { observer } from 'mobx-react';
import { isNil } from 'lodash/fp';
import connect from 'share/enhancers/Izenda.Connect';
import localizer from 'share/localization/Izenda.Localizer';

import Panel from 'share/components/controls/Izenda.Panel';
import Validator from 'share/components/hoc/Izenda.Validator';
import Input from 'share/components/controls/Izenda.Input';
import Select from 'share/components/controls/multiSelectBox/Izenda.Select';
import TenantConstants from 'logic/data/Izenda.TenantConstants';
import TenantDetailModules from './Izenda.TenantDetailModules';
import TenantDetails from 'logic/models/Izenda.TenantDetails';

const ValidatableInput = Validator()(Input);

/**
 * Tenant Id input control
 */
const TenantIdControl = observer(({ showingTenant, onUpdateTenantId }) => {
  return (
    <div className='form-group'>
      <label>{localizer.getText('TENANT_DETAIL_TAB_TENANT_ID')} <span className="validation required"/></label>
      <ValidatableInput
        className='form-control'
        type='text'
        maxLength='50'
        value={showingTenant.tenantID}
        onChange={onUpdateTenantId}
        validations={TenantConstants.TENANT_ID_CONSTRAINT}
        field='tenantID'/>
    </div>
  );
});

/**
 * Tenant Name input control
 */
const TenantNameControl = observer(({ showingTenant, onUpdateTenantName }) => {
  return (
    <div className='form-group'>
      <label>{localizer.getText('COMMON_LABEL_TENANT_NAME')} <span className="validation required"/></label>
      <ValidatableInput
        className='form-control'
        type='text'
        maxLength='50'
        value={showingTenant.name}
        onChange={onUpdateTenantName}
        validations={TenantConstants.TENANT_NAME_CONSTRAINT}
        field='name'/>
    </div>
  );
});

/**
 * Tenant Description input control
 */
const TenantDescriptionControl = observer(({ showingTenant, onUpdateTenantDescription }) => {
  return (
    <div className='form-group'>
      <label>{localizer.getText('COMMON_LABEL_DESCRIPTION')}</label>
      <ValidatableInput
        className='form-control'
        type='text'
        maxLength='100'
        value={showingTenant.description}
        onChange={onUpdateTenantDescription}/>
    </div>
  );
});

/**
 * Tenant Grouping input control
 */
const TenantGroupingControl = observer(
  /**
   * @param {Object} param
   * @param {TenantDetails} param.showingTenant
   * @param {TenantGroupingList} param.groupingList
   * @param {Function} param.onUpdateTenantGrouping
   */
  ({ showingTenant, groupingList, onUpdateTenantGrouping }) => {
    const updateValue = (value, label) => {
      // Find grouping which we've just added by label (because it may have no id yet)
      let grouping = groupingList.findGrouping(label);
      if (!isNil(grouping)) {
        grouping.setName(label);
      }
      onUpdateTenantGrouping({ target: { value: grouping } });
    };
    const addValue = newObject => {
      if (!groupingList.isGroupingExist(newObject.name))
        groupingList.addNewItem(newObject.name);
    };
    return (
      <div className='form-group'>
        <label>{localizer.getText('COMMON_LABEL_GROUPING')}</label>
        <Select simpleValue
                options={groupingList.groupings}
                labelKey={'name'}
                valueKey={'id'}
                inputProps={{ maxLength: '255' }}
                value={showingTenant.tenantGroup}
                noResultsText=""
                onChange={updateValue}
                onAddNew={addValue}/>
      </div>
    );
});

/**
 * Tenant Detail component
 */
@connect()
export default class TenantDetailTab extends Component {
  static propTypes = {
    store: React.PropTypes.object.isRequired
  };

  render() {
    const { store } = this.props;
    const showingTenant = store.showingTenantCloned;
    const groupingList = store.groupingList;
    const disabled = !showingTenant.active;
    const updateDetail = (tenant, field) => e => tenant.setDataByKey(field, e.target.value);

    return (
      <Panel className="u_marginLeft u_marginRight">
        <fieldset disabled={disabled}>
          <TenantIdControl
            showingTenant={showingTenant}
            onUpdateTenantId={updateDetail(showingTenant, 'tenantID')}/>
          <TenantNameControl
            showingTenant={showingTenant}
            onUpdateTenantName={updateDetail(showingTenant, 'name')}/>
          <TenantDescriptionControl
            showingTenant={showingTenant}
            onUpdateTenantDescription={updateDetail(showingTenant, 'description')}/>
          <TenantGroupingControl
            showingTenant={showingTenant}
            groupingList={groupingList}
            onUpdateTenantGrouping={updateDetail(showingTenant, 'tenantGroup')}/>
        </fieldset>

        {/* modules */}
        <div className="u_paddingTop">
          <TenantDetailModules store={store}/>
        </div>
      </Panel>
    );
  }
}



// WEBPACK FOOTER //
// ./ui/modules/Settings/TenantManagement/Izenda.TenantDetailTab.js